import type { ReactNode, ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import { TooltipButton } from "@/v2-backoffice-client/components/button"
import { useTableContext } from "./table.context"

export type TableSelectionToolbarProps<TData = unknown> = Omit<
  ComponentProps<"div">,
  "children"
> & {
  children?(selectedRows: TData[], resetSelection: () => void): ReactNode
}

/**
 * Floating selection bar - only renders when rows are selected.
 */
function TableSelectionToolbar<TData = unknown>(
  props: TableSelectionToolbarProps<TData>,
) {
  const { className, children, ...divProps } = props
  const { table } = useTableContext<TData>()

  const selectedRowModel = table.getSelectedRowModel()
  const selectedRows = selectedRowModel.rows.map((row) => row.original)
  const count = selectedRows.length

  function resetSelection() {
    table.resetRowSelection()
  }

  if (count === 0) return null

  return (
    <div className={cn("ui-table-selection-toolbar", className)} {...divProps}>
      <TooltipButton
        isStacked
        variant="ghost"
        size="sm"
        iconSrc="cross-small"
        iconClassName="ui-table-selection-toolbar-dismiss-icon"
        className="ui-table-selection-toolbar-dismiss"
        content="Clear the selection"
        onClick={resetSelection}
      >
        <span>{count} selected</span>
      </TooltipButton>

      <Separator
        orientation="vertical"
        className="ui-table-selection-toolbar-separator"
      />

      {children?.(selectedRows, resetSelection)}
    </div>
  )
}

export default TableSelectionToolbar
